<template>
  <div>
    <EventModal
      v-model:visible-event-modal="eventModalVisible"
      :event="idea"
      :event-type="EventType.HLPIdea"
      :title="
        modalTitle ??
        (!!event
          ? t('components.issueModal.idea.titleChange').toString()
          : t('components.issueModal.idea.title').toString())
      "
      :save-and-new-button-title="
        !!event
          ? t('components.issueModal.updateAndTask').toString()
          : t('components.issueModal.createAndTask').toString()
      "
      :original-owner-id="event?.ownerId"
      @save="save"
      @observer:valid="observerValid = $event"
    />
    <CreateTaskModal
      v-if="ideaForTask"
      v-model:visible-action-modal="taskModalVisible"
      :event="ideaForTask"
      @save="taskModalVisible = false"
      @hide="taskModalVisible = false"
    />
    <CreateTaskModal
      v-if="ideaForTask && workFlowModalVisible"
      v-model:visible-action-modal="workFlowModalVisible"
      :default-task="workFlowTask"
      :event="ideaForTask"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { provide, ref, type PropType, nextTick, inject, computed } from "vue";
import { DateTime } from "luxon";
import { useStore } from "vuex";
import {
  BoardIdInjectionKey,
  DefaultDateInjectionKey,
  DefaultEisenhowerPriorityInjectionKey,
  DefaultFlagInjectionKey,
  fallbackEisenhowerPriority,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import {
  EventType,
  useCreateHLPIdeaMutation,
  useUpdateHLPIdeaMutation,
  type IFlagEvent,
  type IEventOption,
  useCreateTaskMutation,
  EisenhowerPriority,
} from "@/graphql";
import EventModal from "@/components/event-action/event/modal/EventModal.vue";
import CreateTaskModal from "@/components/event-action/task/modal/CreateTaskModal.vue";
import { addEvent } from "@/graphql/apolloCacheUtils";
import { CreateInjectionKey } from "@/components/event-action/issue/IssueTypes";
import { createShorthandAction } from "@/modules/sqdc/helper/CreateShorthand";
import type { IdeaEditType } from "@/components/event-action/event/modal/EventEditType";
import { useIdeaConfig, useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import usePropsAsDefault from "@/functions/usePropsAsDefault";

const defaultIdea = (
  occurrenceDate: string,
  flags: IFlagEvent[],
  eisenhowerPriority: EisenhowerPriority,
  ownerId?: number,
): IdeaEditType => ({
  ownerId,
  title: "",
  occurrenceDate,
  ideaIs: "",
  fileIds: [],
  ideaShould: "",
  hlpCategoryId: undefined,
  users: ownerId == null ? [] : [{ id: ownerId, percentage: 100 }],
  flags,
  eisenhowerPriority,
  __typename: "HLPIdea",
});

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<null | IdeaEditType>,
    default: null,
  },
  modalTitle: {
    type: String,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits(["save", "saveIssue", "closeOnSave", "updateEvent", "createEvent"]);
const store = useStore();
const observerValid = ref(false);

const eventModalVisible = ref(props.visible);
const taskModalVisible = ref(false);
const workFlowModalVisible = ref(false);
const workFlowTask = ref<ReturnType<typeof createShorthandAction>>();

const loginUserId: number | undefined = store.getters["login/isAnonym"]
  ? undefined
  : (store.getters["login/userId"] as number);

const defaultFlags = inject(DefaultFlagInjectionKey, ref([]));
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: DateTime.now().toISO(), editable: true })),
);

const defaultEisenhowerPriority = inject(
  DefaultEisenhowerPriorityInjectionKey,
  ref(fallbackEisenhowerPriority),
);

const idea = ref<IdeaEditType>(
  defaultIdea(
    defaultDate.value.isoString,
    defaultFlags.value.map((flag) => ({ id: flag.id })),
    defaultEisenhowerPriority.value,
    loginUserId,
  ),
);

const ideaForTask = usePropsAsDefault({ dynamicProps: computed(() => props.event), replace: true });

const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));
const { siteId } = useProvidedBoardContext();

const { resultIdeaConfig } = useIdeaConfig(siteId);

provide(
  CreateInjectionKey,
  computed(() => props?.event?.id == null),
);

const { mutate: createIdeaMutation } = useCreateHLPIdeaMutation({});
const { mutate: updateIdeaMutation } = useUpdateHLPIdeaMutation({});
const { mutate: crateTaskMutation } = useCreateTaskMutation({});

const ideaInputFromIdea = () => {
  return {
    occurrenceDate: idea.value.occurrenceDate,
    ownerId: idea.value.ownerId!,
    flags: idea.value.flags?.map(({ id }) => ({ id })),
    ideaIs: idea.value.ideaIs,
    ideaShould: idea.value.ideaShould,
    hlpCategoryId: idea.value.hlpCategoryId!,
    users: idea.value.users,
    fileIds: idea.value.fileIds,
    title: idea.value.title,
    eisenhowerPriority: idea.value.eisenhowerPriority,
  };
};

const create = async () => {
  if (observerValid.value !== true) {
    return;
  }
  if (boardId.value == null) {
    throw Error("board missing");
  }
  const newIdea = await createIdeaMutation(
    {
      boardId: boardId.value,
      idea: ideaInputFromIdea(),
    },
    {
      update: (_cache, { data }) => {
        if (data?.CreateHLPIdea != null) {
          addEvent(data.CreateHLPIdea, boardId.value!);
        }
      },
    },
  );
  if (newIdea?.data?.CreateHLPIdea) {
    ideaForTask.value = {
      ...newIdea.data.CreateHLPIdea,
      fileIds: newIdea.data.CreateHLPIdea.Folder?.files.map((file) => file.id) ?? [],
      users: newIdea.data.CreateHLPIdea.users.map((user) => ({
        id: user.id,
        percentage: user.HLPIdeaUserConnection?.percentage ?? 0,
      })),
    };
    if (resultIdeaConfig.value?.workflow != null) {
      const startNode = resultIdeaConfig.value?.workflow.drawFlowNodes.find(
        (node) => node.isStartNode,
      );
      if (startNode != null && startNode.shorthand != null && ideaForTask.value != null) {
        const task = createShorthandAction(
          ideaForTask.value,
          crateTaskMutation,
          startNode.id,
          startNode.shorthand,
        );
        if (task != null) {
          await nextTick();
          workFlowTask.value = task;
          workFlowModalVisible.value = true;
        }
      }
    }
    emit("save", {
      eventId: newIdea.data.CreateHLPIdea.id,
      eventType: EventType.HLPIdea,
    });
    emit("saveIssue", { id: newIdea.data.CreateHLPIdea.id });
    await nextTick();
  }
};

const update = async () => {
  if (observerValid.value !== true) {
    return;
  }
  if (props.event?.id == null) {
    throw new Error("update called without set event");
  }
  await updateIdeaMutation({
    ideaId: props.event.id,
    idea: ideaInputFromIdea(),
  });
  emit("saveIssue", { id: props.event.id });
};

const save = async (openTask: boolean) => {
  eventModalVisible.value = false;
  await (props.event ? update() : create());

  if (openTask === true) {
    taskModalVisible.value = true;
  } else {
    emit("closeOnSave");
  }
  const eventId = props.event?.id ?? ideaForTask.value?.id;
  if (eventId == null) {
    throw Error("eventId is missing");
  }
  const eventIdentifier: IEventOption = {
    eventId,
    eventType: EventType.HLPIdea,
  };
  emit(props.event ? "updateEvent" : "createEvent", eventIdentifier, openTask);
};

const show = () => {
  idea.value = props.event
    ? {
        occurrenceDate: props.event.occurrenceDate,
        ownerId: props.event.ownerId ?? loginUserId,
        flags: props.event.flags,
        ideaIs: props.event.ideaIs,
        ideaShould: props.event.ideaShould,
        fileIds: props.event.fileIds,
        title: props.event.title,
        hlpCategoryId: props.event.hlpCategoryId,
        users: props.event.users.map((el) => ({
          id: el.id,
          percentage: el.percentage,
        })),
        deletedAt: props.event.deletedAt,
        eisenhowerPriority: props.event.eisenhowerPriority,
        __typename: props.event.__typename,
      }
    : defaultIdea(
        DateTime.now().toISO(),
        defaultFlags.value.map((flag) => ({ id: flag.id })),
        defaultEisenhowerPriority.value,
        loginUserId,
      );
  eventModalVisible.value = true;
};

defineExpose({
  show,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
